'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { UserRole } from '@/lib/rbac/permissions';
import { FEATURE_PERMISSIONS, hasNavAccess, hasRouteAccess } from '@/lib/rbac/permissions';

interface AuthContextValue {
  user: any;
  session: any;
  loading: boolean;
  userRole: UserRole | null;
  tenantId: string | null;
  isTenantAdmin: boolean;
  can: typeof FEATURE_PERMISSIONS;
  hasRoute: (pathname: string) => boolean;
  hasNav: (href: string) => boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<{ user: any; role: UserRole | null }>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  userRole: null,
  tenantId: null,
  isTenantAdmin: false,
  can: FEATURE_PERMISSIONS,
  hasRoute: () => false,
  hasNav: () => false,
  signUp: async () => {},
  signIn: async () => ({ user: null, role: null }),
  signOut: async () => {},
  getCurrentUser: async () => null,
  isEmailVerified: () => false,
  getUserProfile: async () => null,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isTenantAdmin, setIsTenantAdmin] = useState(false);
  const supabase = createClient();

  // Track login time and session id for duration calculation
  const loginTimeRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const activityProfileRef = useRef<{ userId: string; email: string; fullName: string | null; role: string | null; tenantId: string | null } | null>(null);
  const countryRef = useRef<string | null>(null);

  // Clear all stale Supabase tokens from localStorage and cookies
  const clearStaleTokens = () => {
    if (typeof window === 'undefined') return;
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('sb-') || k.startsWith('sb_'))
        .forEach((k) => localStorage.removeItem(k));
    } catch {}
    try {
      document.cookie.split(';').forEach((c) => {
        const name = c.trim().split('=')[0];
        if (name.includes('auth-token') || name.startsWith('sb-')) {
          document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
        }
      });
    } catch {}
  };

  const fetchRole = async (currentUser: any): Promise<UserRole | null> => {
    const userId = currentUser?.id;
    if (!userId) return null;
    try {
      const { data: up } = await supabase
        .from('user_profiles')
        .select('role, tenant_id, is_tenant_admin')
        .eq('id', userId)
        .maybeSingle();

      if (up?.role) {
        setUserRole(up.role as UserRole);
        setTenantId(up.tenant_id ?? null);
        setIsTenantAdmin(up.is_tenant_admin ?? false);
        // Cache profile for activity tracking
        activityProfileRef.current = {
          userId,
          email: currentUser.email ?? '',
          fullName: up.full_name ?? null,
          role: up.role,
          tenantId: up.tenant_id ?? null,
        };
        return up.role as UserRole;
      }

      // Fallback: check app_metadata for super_admin
      const metaRole = currentUser?.app_metadata?.role as UserRole | undefined;
      if (metaRole) {
        setUserRole(metaRole);
        activityProfileRef.current = {
          userId,
          email: currentUser.email ?? '',
          fullName: null,
          role: metaRole,
          tenantId: null,
        };
        return metaRole;
      }

      setUserRole(null);
      return null;
    } catch {
      setUserRole(null);
      return null;
    }
  };

  /** Detect country from IP using a free geolocation API (fire-and-forget) */
  const detectCountry = async (): Promise<string | null> => {
    try {
      const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.country_name || data?.country || null;
    } catch {
      return null;
    }
  };

  /** Fire-and-forget activity tracking via server-side API route */
  const trackActivity = async (
    eventType: 'login' | 'logout' | 'session_expired',
    overrides?: { loggedOutAt?: string; sessionDurationSeconds?: number }
  ) => {
    try {
      const profile = activityProfileRef.current;
      if (!profile?.userId || !profile?.email) return;

      await fetch('/api/track-activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: profile.userId,
          tenantId: profile.tenantId,
          email: profile.email,
          fullName: profile.fullName,
          role: profile.role,
          eventType,
          sessionId: sessionIdRef.current,
          country: countryRef.current,
          loggedInAt: eventType === 'login' ? new Date().toISOString() : undefined,
          loggedOutAt: overrides?.loggedOutAt,
          sessionDurationSeconds: overrides?.sessionDurationSeconds,
        }),
      });
    } catch {
      // Fire-and-forget — never throw
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (error) {
          // Clear any stale/invalid tokens (e.g. "Invalid Refresh Token: Refresh Token Not Found")
          clearStaleTokens();
          await supabase.auth.signOut().catch(() => {});
          setSession(null);
          setUser(null);
          setUserRole(null);
          setTenantId(null);
          setIsTenantAdmin(false);
          setLoading(false);
          return;
        }

        if (session?.user) {
          // Validate the session is not using a stale refresh token by checking expiry
          const isExpired = session.expires_at ? session.expires_at * 1000 < Date.now() : false;
          if (isExpired) {
            clearStaleTokens();
            await supabase.auth.signOut().catch(() => {});
            setSession(null);
            setUser(null);
            setUserRole(null);
            setTenantId(null);
            setIsTenantAdmin(false);
            setLoading(false);
            return;
          }
          setSession(session);
          setUser(session?.user ?? null);
          await fetchRole(session.user);
        } else {
          setSession(null);
          setUser(null);
        }
      } catch (err: any) {
        if (!isMounted) return;
        // Handle "Invalid Refresh Token" and similar auth errors gracefully
        const isRefreshTokenError =
          err?.message?.toLowerCase().includes('refresh token') ||
          err?.message?.toLowerCase().includes('invalid token') ||
          err?.name === 'AuthApiError';
        if (isRefreshTokenError) {
          clearStaleTokens();
        }
        await supabase.auth.signOut().catch(() => {});
        setSession(null);
        setUser(null);
        setUserRole(null);
        setTenantId(null);
        setIsTenantAdmin(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;

      if ((_event === 'TOKEN_REFRESHED' && !session) || _event === 'SIGNED_OUT') {
        // Track logout/session expiry
        if (activityProfileRef.current?.userId) {
          const now = Date.now();
          const durationSeconds = loginTimeRef.current
            ? Math.round((now - loginTimeRef.current) / 1000)
            : undefined;
          const eventType = _event === 'SIGNED_OUT' ? 'logout' : 'session_expired';
          trackActivity(eventType, {
            loggedOutAt: new Date(now).toISOString(),
            sessionDurationSeconds: durationSeconds,
          });
        }
        loginTimeRef.current = null;
        sessionIdRef.current = null;
        activityProfileRef.current = null;

        clearStaleTokens();
        setSession(null); setUser(null); setUserRole(null); setTenantId(null); setIsTenantAdmin(false); setLoading(false);
        if (_event === 'TOKEN_REFRESHED') {
          // Token refresh failed — force sign out to clear invalid state
          supabase.auth.signOut().catch(() => {});
        }
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Skip fetchRole on TOKEN_REFRESHED if role is already loaded — avoids redundant DB calls
        const shouldFetchRole =
          _event === 'SIGNED_IN' ||
          _event === 'INITIAL_SESSION' ||
          userRole === null;
        if (shouldFetchRole) {
          fetchRole(session.user).finally(() => { if (isMounted) setLoading(false); });
        } else {
          setLoading(false);
        }
      } else {
        setUserRole(null); setTenantId(null); setIsTenantAdmin(false); setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: metadata?.fullName || '', avatar_url: metadata?.avatarUrl || '' },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    if (data.user) {
      await supabase.from('user_profiles').upsert({
        id: data.user.id, email,
        full_name: metadata?.fullName || '',
        role: (metadata?.role as UserRole) || 'viewer',
        is_active: true,
      }, { onConflict: 'id' });
    }
    return data;
  };

  const signIn = async (email: string, password: string): Promise<{ user: any; role: UserRole | null }> => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    if (data.user) {
      // Fetch user profile to get tenant_id
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('tenant_id, role, full_name')
        .eq('id', data.user.id)
        .maybeSingle();

      const tenantIdForCheck = profile?.tenant_id;

      // Only validate subscription for tenant users (not super_admin)
      if (tenantIdForCheck && profile?.role !== 'super_admin') {
        // The subscriptions table is super-admin only; this database function returns just
        // true/false for the caller's own company (true when it has no subscription row).
        // Only an explicit false blocks the login; an error or missing result lets it through.
        const { data: subscriptionActive } = await supabase.rpc('current_tenant_subscription_active');

        if (subscriptionActive === false) {
          // Sign out immediately — do not create a session
          await supabase.auth.signOut().catch(() => {});

          // Log the failed login attempt
          try {
            await supabase.from('audit_logs').insert({
              action: 'LOGIN_FAILED',
              entity_type: 'auth',
              entity_id: data.user.id,
              tenant_id: tenantIdForCheck,
              performed_by: data.user.id,
              details: {
                reason: 'Inactive Subscription',
                email: data.user.email,
                tenant_id: tenantIdForCheck,
              },
            });
          } catch {
            // Audit log failure should not block the error response
          }

          const err = new Error('INACTIVE_SUBSCRIPTION');
          (err as any).code = 'INACTIVE_SUBSCRIPTION';
          throw err;
        }
      }

      // Set up activity tracking refs before tracking login
      const newSessionId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      loginTimeRef.current = Date.now();
      sessionIdRef.current = newSessionId;
      activityProfileRef.current = {
        userId: data.user.id,
        email: data.user.email ?? email,
        fullName: profile?.full_name ?? null,
        role: profile?.role ?? null,
        tenantId: profile?.tenant_id ?? null,
      };

      // Detect country before tracking login
      countryRef.current = await detectCountry();

      // Track login event (fire-and-forget)
      trackActivity('login');
    }

    let role: UserRole | null = null;
    if (data.user) role = await fetchRole(data.user);
    return { user: data.user, role };
  };

  const signOut = async () => {
    // Track logout before signing out
    if (activityProfileRef.current?.userId) {
      const now = Date.now();
      const durationSeconds = loginTimeRef.current
        ? Math.round((now - loginTimeRef.current) / 1000)
        : undefined;
      await trackActivity('logout', {
        loggedOutAt: new Date(now).toISOString(),
        sessionDurationSeconds: durationSeconds,
      });
    }
    loginTimeRef.current = null;
    sessionIdRef.current = null;
    activityProfileRef.current = null;

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase.from('user_profiles').select('*').eq('id', user.id).single();
    if (error) throw error;
    return data;
  };

  const can = FEATURE_PERMISSIONS;
  const hasRoute = (pathname: string) => hasRouteAccess(userRole, pathname);
  const hasNav = (href: string) => hasNavAccess(userRole, href);

  const value: AuthContextValue = {
    user, session, loading, userRole, tenantId, isTenantAdmin,
    can, hasRoute, hasNav, signUp, signIn, signOut, getCurrentUser, isEmailVerified, getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
